import { AsyncPipe, NgTemplateOutlet, TitleCasePipe } from '@angular/common';
import {
  AfterViewChecked,
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  inject,
  OnDestroy,
  Output,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatAnchor } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatProgressBar } from '@angular/material/progress-bar';
import { MatTab, MatTabChangeEvent, MatTabContent, MatTabGroup } from '@angular/material/tabs';
import { ActivatedRoute, Router } from '@angular/router';
import {
  FwButtonComponent,
  FwDialogService,
  FwIconButtonComponent,
  FwMenuComponent,
  FwMenuContainerComponent,
  FwMenuItemComponent,
  FwMenuSeparatorComponent,
  FwMenuTriggerDirective,
  FwPopoverComponent,
  FwTooltipDirective,
} from '@flywheel-io/vision';
import pick from 'lodash/pick';
import {
  BehaviorSubject,
  combineLatest,
  forkJoin,
  of,
  Subject as RxSubject,
  Subscription,
  SubscriptionLike,
} from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, first, map, switchMap, take, tap } from 'rxjs/operators';

import { ContainerModule } from 'app/src/containers/container.module';
import { FileModule } from 'app/src/files/file.module';
import { AnalysisListComponent } from 'app/src/jobs/analysis-list/analysis-list.component';
import { JobProvenanceComponent } from 'app/src/jobs/job-provenance/job-provenance.component';
import { SharedModule } from 'app/src/shared/shared.module';

import { ChangeManagerService } from '../../change-manager.service';
import { ConfigService } from '../../config.service';
import { ContainerApplicationsComponent } from '../../containers/applications/container-applications.component';
import { FileDownloadDialog } from '../../containers/file-download-dialog/file-download-dialog.component';
import { ApiParams, CoreApiService } from '../../core-api.service';
import { DialogService } from '../../dialogs';
import { GearService } from '../../gears/gear.service';
import { LoggingService } from '../../logging.service';
import { ProfileService } from '../../profile/profile.service';
import { ProjectsService } from '../../projects/projects.service';
import { getContainerProjectId, getRoleMatrix } from '../../shared/container.utils';
import { RoleMatrix } from '../../shared/enums/container-access.enum';
import { ContainerType } from '../../shared/enums/container-type.enum';
import { PermissionAction } from '../../shared/enums/permission-action.enum';
import { SiteRole } from '../../shared/enums/site-role.enum';
import { ReaderTaskContainerType } from '../../shared/enums/task.enum';
import { Collection } from '../../shared/models/collection.model';
import { Container, ContainerInfoValue } from '../../shared/models/container.model';
import { DownloadRequest } from '../../shared/models/download.model';
import { Group } from '../../shared/models/group.model';
import { Profile } from '../../shared/models/profile.model';
import { Project, ProjectStats } from '../../shared/models/project.model';
import { Session } from '../../shared/models/session.model';
import { ViewerApp } from '../../shared/models/site-settings.model';
import { Subject } from '../../shared/models/subject.model';
import { SidebarService } from '../../sidebar.service';
import { CreateReaderTaskDialogComponent } from '../../task-manager/create-reader-task-dialog/create-reader-task-dialog.component';
import { ViewerService } from '../../viewers/viewer.service';
import { ProjectInfoComponent } from '../project-info/project-info.component';
import { SessionService } from '../session.service';
import { SessionDataComponent } from '../session-data/session-data.component';
import { SessionDataState } from '../session-data/session-data.state';
import { SessionInfoComponent } from '../session-info/session-info.component';
import { SubjectInfoComponent } from '../subject-info/subject-info.component';
import { SubjectSessionsComponent } from '../subject-sessions/subject-sessions.component';

type StatusError = Error & { status?: number };

const sessionTabs = new Set(['data', 'annotation', 'subject', 'project', 'collection', 'analyses', 'provenance']);
const defaultSessionTab = Array.from(sessionTabs)[0];

const subjectTabs = new Set(['subject', 'sessions', 'project', 'collection', 'analyses', 'provenance']);
const defaultSubjectTab = Array.from(subjectTabs)[0];

@Component({
  selector: 'session-detail',
  templateUrl: './session-detail.component.html',
  styleUrls: ['./session-detail.component.scss'],
  providers: [SessionDataState],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    AnalysisListComponent,
    AsyncPipe,
    ContainerApplicationsComponent,
    ContainerModule,
    FileModule,
    FwButtonComponent,
    FwIconButtonComponent,
    FwMenuComponent,
    FwMenuContainerComponent,
    FwMenuItemComponent,
    FwMenuSeparatorComponent,
    FwMenuTriggerDirective,
    FwPopoverComponent,
    FwTooltipDirective,
    JobProvenanceComponent,
    MatAnchor,
    MatIcon,
    MatProgressBar,
    MatTab,
    MatTabContent,
    MatTabGroup,
    NgTemplateOutlet,
    ProjectInfoComponent,
    SessionDataComponent,
    SessionInfoComponent,
    SharedModule,
    SubjectInfoComponent,
    SubjectSessionsComponent,
    TitleCasePipe,
  ],
})
export class SessionDetailComponent implements AfterViewInit, OnDestroy, AfterViewChecked {
  private config = inject(ConfigService);
  private dialog = inject(DialogService);
  private fwDialog = inject(FwDialogService);
  private coreApi = inject(CoreApiService);
  private gearService = inject(GearService);
  private logger = inject(LoggingService);
  private profileService = inject(ProfileService);
  projectsService = inject(ProjectsService);
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);
  private sessionService = inject(SessionService);
  private sidebar = inject(SidebarService);
  readonly state = inject(SessionDataState);
  private viewerService = inject(ViewerService);
  private changeManager = inject(ChangeManagerService);
  private destroyRef = inject(DestroyRef);

  static selectedTabIdTimeout = 20; // ms

  bidsView$ = this.sidebar.bidsViewActive$.asObservable();
  collectionId$ = new BehaviorSubject(null);
  container$ = new RxSubject<Container>();
  containerId$ = new BehaviorSubject(null);
  containerType: ContainerType.Session | ContainerType.Subject;
  ContainerType = ContainerType;
  collection?: Collection;
  collectionRoleMatrix?: RoleMatrix;
  dicomViewer = {
    acquisitionIds: [],
    fileIds: [],
    ohifEnabled: this.viewerService.isOhifViewerEnabled(),
  };
  error: StatusError = null;
  group: Group;
  permissions = {
    createContainer: false,
    deleteContainer: false,
    downloadFiles: false,
    editContainerMetadata: false,
    runGear: false,
    tasksModify: false,
    uploadFiles: false,
    viewAnalyses: false,
    viewContainerMetadata: true,
    viewFileMetadata: false,
    viewFiles: false,
    viewJobs: false,
    viewNotes: false,
    viewTags: false,
  };
  project: Project;
  projectId$ = new BehaviorSubject(null);
  projectPermissions = new Set<PermissionAction>();
  projectStats: ProjectStats;
  session?: Session;
  subject: Subject;
  viewerApps = [];
  protected tasksRefactorEnabled = this.config.isFeatureEnabled('tasks_refactor');
  private isSubjectListView = false;

  @Output() refreshSessions = new EventEmitter<void>();
  @ViewChild(MatTabGroup) tabGroup: MatTabGroup;
  @ViewChildren(MatTab, { read: ElementRef }) visibleTabs: QueryList<ElementRef<HTMLElement>>;

  get visibleTabIds(): string[] {
    return this.visibleTabs ? this.visibleTabs.map((tab) => tab.nativeElement.getAttribute('tab-id')) : [];
  }

  get isSearchView(): boolean {
    return this.router.url.startsWith('/search');
  }

  get container(): Session | Subject {
    return this.containerType === ContainerType.Subject ? this.subject : this.session;
  }

  /**
   * Selecting a session row in the subject list shows session details, but the analyses stay subject
   * scoped: an analysis run on a session belongs in that session's own Analyses tab, not in its
   * parent subject's.
   */
  protected get analysesContainer(): Session | Subject {
    return this.isSubjectListView ? this.subject : this.container;
  }

  protected get analysesContainerType(): ContainerType.Session | ContainerType.Subject {
    return this.isSubjectListView ? ContainerType.Subject : this.containerType;
  }

  get sessionTimestamp(): string {
    if (!this.session?.timestamp) {
      return undefined;
    }
    const cacheKey = `${this.session.timestamp}-${this.session.timezone}-${this.state.originalTimezone$.value}`;
    if (!this.timestamps[cacheKey]) {
      this.timestamps[cacheKey] = this.sessionService.formattedTimestamp(this.session);
    }
    return this.timestamps[cacheKey];
  }

  get projectBids(): ContainerInfoValue {
    return this.project?.info?.BIDS;
  }

  get collectionHref(): string {
    return `/collection/${this.collection?._id}`;
  }

  targetTab$ = new BehaviorSubject('');
  activeTabIndex$ = new BehaviorSubject(0);

  private subscriptions = {
    collection: Subscription.EMPTY,
    container: Subscription.EMPTY,
    dicomAcquisitions: Subscription.EMPTY,
    location: Subscription.EMPTY as SubscriptionLike,
    project: Subscription.EMPTY,
    searchSelection: Subscription.EMPTY,
    settings: Subscription.EMPTY,
    tab: Subscription.EMPTY,
    projectId: Subscription.EMPTY,
  };
  private timestamps: Record<string, string> = {}; // {session.timestamp}-{session-timezone}-{originalTimezone$.value} -> formatted timestamp

  constructor() {
    this.subscriptions.settings = this.profileService.settings$.subscribe((settings) => {
      this.state.originalTimezone$.next(settings.originalTimezones);
      this.state.runGearRulesOnMove$.next(settings.runGearRulesOnMove);
    });

    this.containerId$.pipe(distinctUntilChanged()).subscribe(() => {
      this.loadContainer();
    });

    this.subscriptions.projectId = this.projectId$
      .pipe(
        distinctUntilChanged(),
        switchMap((projectId) => (projectId ? this.projectsService.getProjectPermissions(projectId) : of([]))),
      )
      .subscribe((permissions) => {
        this.projectPermissions = new Set(permissions);
        this.permissions = {
          createContainer: this.projectPermissions.has(PermissionAction.ContainersCreateHierarchy),
          deleteContainer: this.projectPermissions.has(PermissionAction.ContainersDeleteHierarchy),
          downloadFiles: this.projectPermissions.has(PermissionAction.FilesDownload),
          editContainerMetadata: this.projectPermissions.has(PermissionAction.ContainersModifyMetadata),
          runGear: this.projectPermissions.has(PermissionAction.JobsManage),
          tasksModify: this.projectPermissions.has(PermissionAction.TasksModify),
          uploadFiles: this.projectPermissions.has(PermissionAction.FilesCreateSingle),
          viewAnalyses: this.projectPermissions.has(PermissionAction.AnalysesViewMetadata),
          viewContainerMetadata: this.projectPermissions.has(PermissionAction.ContainersViewMetadata),
          viewFileMetadata: this.projectPermissions.has(PermissionAction.FilesViewMetadata),
          viewFiles: this.projectPermissions.has(PermissionAction.FilesViewContents),
          viewJobs: this.projectPermissions.has(PermissionAction.JobsView),
          viewNotes: this.projectPermissions.has(PermissionAction.NotesView),
          viewTags: this.projectPermissions.has(PermissionAction.TagsView),
        };
        this.loadProject();
      });

    this.collectionId$.pipe(distinctUntilChanged()).subscribe(() => {
      this.loadCollection();
    });

    // if either this.session or this.subject is updated get the associated apps for that container
    this.container$
      .pipe(
        switchMap((container) => {
          const projectId = getContainerProjectId(container);
          return this.viewerService.getAssociatedApps({ container, projectId });
        }),
      )
      .subscribe((apps) => {
        this.viewerApps = apps.filter((app) => app.type !== 'form');
      });

    if (this.isSearchView) {
      this.subscriptions.searchSelection = this.sidebar.search.selectedContainer$.subscribe(() => {
        this.captureSearchState();
      });
    } else {
      this.captureRouteState();
      this.subscriptions.location = combineLatest([
        this.activatedRoute.params,
        this.activatedRoute.queryParams,
      ]).subscribe(() => {
        this.captureRouteState();
      });
    }

    this.subscriptions.dicomAcquisitions = this.state.dicomAcquisitions$.subscribe((dicomAcquisitions) => {
      // Delay update until after component settles, this prevents a ExpressionChangedAfterItHasBeenCheckedError error
      // when our child component updates state
      setTimeout(() => {
        this.dicomViewer.acquisitionIds = dicomAcquisitions.map((acq) => acq._id);
        this.dicomViewer.fileIds = dicomAcquisitions.reduce(
          (fileIds, acq) => fileIds.concat(acq.files.filter((file) => file.type === 'dicom').map((file) => file._id)),
          [],
        );
      });
    });
  }

  ngAfterViewInit(): void {
    // finding the tab index of a given tab is dependent on what is currently rendered
    // so this needs to be inside afterViewInit or later
    // otherwise the ViewChildren `visibleTabs` will be undefined
    this.subscriptions.tab = combineLatest([this.targetTab$, this.visibleTabs.changes])
      .pipe(debounceTime(200)) // this prevents tab fighting when reacting to route changes
      .subscribe(([targetTab]) => {
        const visibleIndex = this.visibleTabIds.indexOf(targetTab);
        const targetIndex = visibleIndex !== -1 ? visibleIndex : 0;
        this.activeTabIndex$.next(targetIndex);
      });
  }

  ngAfterViewChecked(): void {
    const handleClick = Symbol.for('MatTabGroup_handleClick');
    if (this.tabGroup && !this.tabGroup[handleClick]) {
      // patch tab click handler and store original function in a symbol property
      this.tabGroup[handleClick] = this.tabGroup._handleClick;
      this.tabGroup._handleClick = (...args) => {
        this.handleTabClick(() => this.tabGroup[handleClick](...args));
      };
    }
  }

  ngOnDestroy(): void {
    for (const subscription of Object.values(this.subscriptions)) {
      subscription.unsubscribe();
    }
  }

  private captureSearchState(): void {
    const searchContainer = this.sidebar.search.selectedContainer$.value;
    this.containerType = searchContainer?.type;
    this.isSubjectListView = false;
    this.containerId$.next(searchContainer?.id);

    if (this.sidebar.search.acqId$?.value || this.sidebar.search.context === 'file') {
      this.changeTab('data');
    } else if (this.sidebar.search.analysisLabel$.value) {
      this.changeTab('analyses');
    } else {
      const targetTab =
        this.containerType === ContainerType.Subject
          ? mapToSubjectTab(this.sessionService.lastSessionDetailsTab)
          : mapToSessionTab(this.sessionService.lastSessionDetailsTab);
      this.changeTab(targetTab);
    }
  }

  private captureRouteState(): void {
    const { collectionId, entityId, subjectId } = this.activatedRoute.snapshot.params;
    const { tab, sessionId, subjectTab } = this.activatedRoute.snapshot.queryParams;
    let containerId = null;
    let targetTab = tab;

    const isRootSessions = this.router.url.startsWith('/sessions');
    const isProjectSessions = this.activatedRoute.snapshot.url[0].path === 'sessions';

    // the subject list keeps the subject in the route and carries the selected session in `sessionId`
    this.isSubjectListView = !isRootSessions && !isProjectSessions && !collectionId && Boolean(sessionId);

    if (isRootSessions || isProjectSessions || collectionId || sessionId || subjectTab === 'session') {
      this.containerType = ContainerType.Session;
      // prefer sessionId if available here
      containerId = sessionId || entityId || subjectId || null;
      targetTab = mapToSessionTab(tab);
    } else {
      this.containerType = ContainerType.Subject;
      containerId = entityId || sessionId || subjectId || null;
      targetTab = mapToSubjectTab(tab);
    }

    this.containerId$.next(containerId);
    this.collectionId$.next(collectionId || null);

    this.changeTab(targetTab);
  }

  /**
   * Decide if we allow the tab to change by calling `next`.
   */
  private handleTabClick(next: () => void): void {
    if (!this.changeManager?.dirty) {
      return next();
    }
    this.changeManager
      .promptDecision()
      .pipe(filter((proceed) => proceed))
      .subscribe(() => next());
  }

  /**
   * This fires if the user selects a tab, or if the index is changed programatically.
   */
  changeTab(targetTab: string): void {
    this.targetTab$.next(targetTab);

    const currentRouteTab = this.activatedRoute.snapshot.queryParams.tab;

    // if there's no tab currently selected we should overwrite that history entry
    // makes for easier, history.back
    const shouldReplaceHistory = !currentRouteTab;

    if (currentRouteTab !== targetTab) {
      this.router.navigate([], {
        relativeTo: this.activatedRoute,
        queryParamsHandling: 'merge',
        queryParams: { tab: targetTab },
        replaceUrl: shouldReplaceHistory,
      });
    }
  }

  onTabChange(change: MatTabChangeEvent): void {
    const targetTabName = this.visibleTabIds[change.index];

    this.changeTab(targetTabName);
  }

  loadContainer(): void {
    this.error = null;
    this.state.dicomAcquisitions$.next([]);
    this.subscriptions.container.unsubscribe();
    const containerId = this.containerId$.value;
    if (!containerId) {
      this.session = null;
      this.subject = null;
      return;
    }
    const checkProjectId = tap((container: Container) => {
      const params = this.activatedRoute.snapshot.params;

      if (params.projectId && params.projectId !== container.parents.project) {
        const error = new Error('Container does not belong within the project in the page route.');
        throw Object.assign(error, { status: 404 });
      }
    });
    if (this.containerType === ContainerType.Subject) {
      this.session = null;
      this.subscriptions.container = this.coreApi.subjects
        .get(containerId, { join_avatars: true, join: 'origin' })
        .pipe(checkProjectId)
        .subscribe({
          next: (subject: Subject) => {
            this.subject = subject;
            this.projectId$.next(subject.parents.project);
            this.container$.next(subject);
          },
          error: (error: StatusError) => {
            if (error.status > 404) {
              this.logger.error(error);
            }
            this.error = error;
            this.subject = null;
            this.projectId$.next(null);
          },
        });
    } else {
      this.subscriptions.container = this.coreApi.sessions
        .get(containerId, { join_avatars: true, join: 'origin' })
        .pipe(checkProjectId)
        .subscribe({
          next: (session: Session) => {
            this.session = session;
            this.subject = session.subject;
            this.projectId$.next(session.parents.project);
            this.container$.next(session);
          },
          error: (error: StatusError) => {
            if (error.status > 404) {
              this.logger.error(error);
            }
            this.error = error;
            this.session = null;
            this.subject = null;
            this.projectId$.next(null);
          },
        });
    }
  }

  loadProject(): void {
    this.subscriptions.project.unsubscribe();
    const projectId = this.projectId$.value;
    if (!projectId) {
      this.project = null;
      return;
    }
    this.subscriptions.project = forkJoin([
      this.coreApi.projects.get(projectId, { join_avatars: true, join: 'origin' }).pipe(
        switchMap((project) =>
          this.projectsService.getCachedGroup(project.group).pipe(
            map((group) => {
              this.group = group;
              return project;
            }),
          ),
        ),
      ),
      this.profileService.getProfile().pipe(
        first((data) => Boolean(data)),
        switchMap((profile: Profile) => {
          const params: ApiParams = { filter: `_id=${projectId}`, stats: true };
          return this.coreApi.projects.query(params).pipe(
            switchMap((response) => {
              if (response.results.length === 0 && SiteRole.isSiteAdmin(profile)) {
                // FLYW-7312 toggle manage mode if site admin
                this.profileService.showAllData = true;
                return this.coreApi.projects.query({ ...params, exhaustive: true });
              }
              return of(response);
            }),
          );
        }),
        map((response) => response.results[0]),
        map((stats) =>
          stats
            ? pick(stats, 'noncompliant_session_count', 'session_count', 'subject_count')
            : { noncompliant_session_count: 0, session_count: 0, subject_count: 0 },
        ),
      ),
    ]).subscribe({
      next: ([project, stats]: [Project, ProjectStats]) => {
        this.project = project;
        this.projectStats = stats;
      },
      error: (error) => {
        this.logger.error(error);
        this.group = null;
        this.project = null;
        this.projectStats = null;
      },
    });
  }

  loadCollection(): void {
    this.subscriptions.collection.unsubscribe();
    const collectionId = this.collectionId$.value;
    if (!collectionId) {
      this.collection = null;
      return;
    }
    this.subscriptions.collection = forkJoin([
      this.coreApi.collections.get(collectionId, { join_avatars: true, join: 'origin' }),
      this.profileService.getProfile().pipe(first((data) => Boolean(data))),
    ]).subscribe({
      next: ([collection, user]) => {
        this.collection = collection;
        this.collectionRoleMatrix = getRoleMatrix(collection, user);
      },
      error: (error) => {
        this.logger.error(error);
        this.collection = null;
        this.collectionRoleMatrix = null;
      },
    });
  }

  viewDicomStudy(): void {
    this.viewerService.ohifViewerDialog({
      acquisitionIds: this.dicomViewer.acquisitionIds,
      fileIds: this.dicomViewer.fileIds,
      projectId: this.project._id,
      sessionId: this.session._id,
    });
  }

  protected createReaderTask(): void {
    this.fwDialog
      .openDialog(CreateReaderTaskDialogComponent, {
        closeOnDestroy: true,
        disableClose: true,
        data: {
          projectId: this.projectId$.value,
          containerType: ReaderTaskContainerType.Session,
          containerId: this.containerId$.value,
        },
      })
      .closed.pipe(take(1))
      .subscribe();
  }

  runGear(): void {
    this.gearService
      .runGear({
        containers: [this.session],
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();
  }

  createAcquisition(): void {
    this.sessionService.createAcquisition(this.session);
  }

  downloadFiles(): void {
    const downloadRequest: DownloadRequest = {
      nodes: [{ level: this.containerType, _id: this.container._id }],
    };
    if (this.collection?._id) {
      downloadRequest.collection_filters = [this.collection._id];
    }
    this.dialog.open(FileDownloadDialog, {
      data: { requestBody: downloadRequest },
    });
  }

  viewContainer(app: ViewerApp): void {
    this.viewerService
      .view({
        app: app,
        container: this.container,
      })
      .subscribe((result) => {
        if (result?.dirty) {
          this.reloadSessions();
        }
      });
  }

  toggleBidsView(bidsViewActive: boolean): void {
    this.sidebar.bidsViewActive$.next(bidsViewActive);
  }

  toggleOriginalTimezones(originalTimezones: boolean): void {
    this.profileService.updateSettings({ originalTimezones });
  }

  toggleRunGearRules(runGearRulesOnMove: boolean): void {
    this.profileService.updateSettings({ runGearRulesOnMove });
  }

  delete(): void {
    if (this.containerType === ContainerType.Session) {
      this.sessionService.deleteSession(this.container).subscribe(() => {
        this.refreshSessions.emit();
        this.router.navigate(['..'], {
          relativeTo: this.activatedRoute,
          replaceUrl: true,
          queryParamsHandling: 'preserve',
        });
      });
    } else {
      this.sessionService.deleteSubject(this.container).subscribe();
    }
  }

  reloadSessions(): void {
    this.loadContainer();
    // Changes to a session sometimes mean UI elements need an update (ex timestamp, session name)
    this.sessionService.reloadCurrentSessions();
  }
}

function mapToSessionTab(tab: string): string {
  switch (tab) {
    case 'sessions':
      return 'data';
    default:
      return (sessionTabs.has(tab) && tab) || defaultSessionTab;
  }
}

function mapToSubjectTab(tab: string): string {
  switch (tab) {
    case 'data':
      return 'sessions';
    case 'annotation':
      return 'subject';
    default:
      return (subjectTabs.has(tab) && tab) || defaultSubjectTab;
  }
}
